/**
 * A quick live's link (`/live/<code>`), as the confirmed prototype's quick door draws it: a picture blurred behind (the
 * room's poster once its recording has one, the host's own photo until then), the host's face with LIVE, the talk, who
 * it is with and how many are here, and one tap in. A seat this browser already has for it goes
 * straight to the room; a person it knows from another talk here joins with one tap; anyone else gives a first name
 * and an email. After that it is the room any live has.
 */
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { guestLook } from "../../shared/guest-look";
import { measurementActive } from "../../shared/measure";
import { metaContent } from "../../shared/meta-events";
import type { TroubleKind } from "../../shared/trouble";
import type { LiveDoorResponse, PublicRegisterResponse } from "../../shared/types";
import { ApiError, api } from "../lib/api";
import { installMeasurement } from "../lib/measure";
import { isTenantHost } from "../lib/tenant";
import { fmt } from "../lib/when";
import { DoorBackdrop } from "./live/DoorBackdrop";
import { doorAttribution, JoinForm } from "./live/JoinForm";
import { troubleForPage } from "./PublicPageView";
import { Brand, useGuestGround } from "./room/chrome";
import { GIcon } from "./room/GIcon";
import { TroublePage } from "./TroublePage";
import { keptTokens } from "./watchToken";
import "../styles/guest.css";

/** The chip on the host's face, by where the live is. */
const CHIP: Record<LiveDoorResponse["state"], string> = { live: "LIVE", waiting: "STARTING SOON", ended: "ENDED" };

/** "with Maya Torres · 24 here": who it is with, and how many are in while it is on. */
export function withLine(host: string, door: Pick<LiveDoorResponse, "state" | "here">): string {
  return door.state === "live" && door.here > 0 ? `with ${host} · ${fmt(door.here)} here` : `with ${host}`;
}

export function LiveDoorView(): ReactNode {
  const { code = "" } = useParams();
  const navigate = useNavigate();
  const [door, setDoor] = useState<LiveDoorResponse | null>(null);
  const [trouble, setTrouble] = useState<TroubleKind | null>(null);
  const [notYou, setNotYou] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const openedAt = useRef(Date.now());

  const enter = (res: PublicRegisterResponse) => navigate(res.redirect, { replace: true });

  useEffect(() => {
    let alive = true;
    api.public
      .door(code, keptTokens())
      .then((d) => {
        if (!alive) return;
        if (d.seat_token) {
          navigate(`/w/${encodeURIComponent(d.slug)}/watch?t=${encodeURIComponent(d.seat_token)}`, { replace: true });
          return;
        }
        if (isTenantHost(window.location.hostname) && measurementActive(d.measurement)) {
          installMeasurement(d.measurement, metaContent(d.slug, d.webinar_id));
        }
        setDoor(d);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        const e = err instanceof ApiError ? err : null;
        setTrouble(troubleForPage(e?.status ?? 0, e ? { reason: e.reason } : null, navigator.onLine && e?.status !== 0));
      });
    return () => {
      alive = false;
    };
  }, [code, navigate]);

  const look = useMemo(() => (door ? guestLook(door.brand) : {}), [door]);
  useGuestGround(look);

  if (trouble) return <TroublePage kind={trouble} />;
  if (!door) {
    return (
      <div className="gst" style={look}>
        <span className="spin" aria-label="Loading" />
      </div>
    );
  }

  const host = door.host.name || door.brand.workspace_name;
  const hostFirst = door.host.first_name || host;
  const ended = door.state === "ended";
  const joinAs = () => {
    if (busy || !door.known) return;
    setBusy(true);
    setError("");
    api.public.joinAs(door.slug, door.known.token, doorAttribution(openedAt.current)).then(enter, (err: unknown) => {
      setError(err instanceof ApiError ? err.message : "Something went wrong. Try again.");
      setBusy(false);
      setNotYou(true);
    });
  };

  return (
    <div className="gst" style={look}>
      <div className="door">
        <DoorBackdrop poster={door.poster_url} avatar={door.host.avatar_url} />
        <div className="door-shade" />
        <header className="top">
          <Brand brand={door.brand} />
          {door.known && !notYou ? (
            <div className="top-r">
              <a className="top-link" href={`/watch/${encodeURIComponent(door.known.token)}`}>
                <GIcon name="home" size={16} /> Your page
              </a>
            </div>
          ) : null}
        </header>
        <div className="qdoor">
          <div className="qd-c">
            <span className="qd-face">
              {door.host.avatar_url ? <img src={door.host.avatar_url} alt={host} /> : <span className="av av-i">{hostFirst.charAt(0)}</span>}
              <span className="chip">{CHIP[door.state]}</span>
            </span>
            <h1>{door.title}</h1>
            <p className="muted">{withLine(host, door)}</p>
            <div style={{ display: "grid", gap: 12, width: "100%", justifyItems: "center" }}>
              {door.known && !notYou ? (
                <>
                  <button className="btn btn-p" type="button" disabled={busy} onClick={joinAs}>
                    {busy ? <span className="spin" /> : <GIcon name="door" size={20} />} {ended ? "Watch the replay" : `Join as ${door.known.first_name}`}
                  </button>
                  <button className="btn-q" type="button" onClick={() => setNotYou(true)}>
                    Not you?
                  </button>
                </>
              ) : (
                <JoinForm
                  slug={door.slug}
                  siteKey={door.turnstile_site_key}
                  button={ended ? "Watch the replay" : "Join now"}
                  note={
                    ended
                      ? `${hostFirst}’s live has ended. The replay plays right here.`
                      : notYou
                        ? `${hostFirst} will know you came. Use the email you signed up with.`
                        : `${hostFirst} will know you came and can send you the replay.`
                  }
                  onIn={enter}
                />
              )}
              {error ? (
                <p className="f-err" role="alert">
                  {error}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
